
// table helpers start
export type Order = "asc" | "desc";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export const handleTableRowClick = (
  event: React.MouseEvent<unknown>,
  id: number,
  selected: readonly number[],
  setSelected: (param: readonly number[]) => void
) => {
  const selectedIndex = selected.indexOf(id);
  let newSelected: readonly number[] = [];

  if (selectedIndex === -1) {
    newSelected = newSelected.concat(selected, id);
  } else if (selectedIndex === 0) {
    newSelected = newSelected.concat(selected.slice(1));
  } else if (selectedIndex === selected.length - 1) {
    newSelected = newSelected.concat(selected.slice(0, -1));
  } else if (selectedIndex > 0) {
    newSelected = newSelected.concat(
      selected.slice(0, selectedIndex),
      selected.slice(selectedIndex + 1)
    );
  }
  setSelected(newSelected);
};

export const handleRequestSort = (
  event: React.MouseEvent<unknown>,
  property: any,
  order: any,
  setOrder: (param: any) => void,
  setOrderBy: (param: any) => void,
  orderBy: any
) => {
  const isAsc = orderBy === property && order === "asc";
  setOrder(isAsc ? "desc" : "asc");
  setOrderBy(property);
};

// table helpers end

export function getInitials(name: string): string {
  if (!name) return "";
  const words = name.trim().split(/\s+/).filter(Boolean);
  const initials = words.map((word) => word[0].toUpperCase());
  return initials.slice(0, 2).join("");
}

export const getRelativeTime = (timestamp: string | number | Date) => {
  const now = new Date();
  const past = new Date(timestamp);
  const diffMs = now.getTime() - past.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffHours < 1) return "Just now";
  if (diffHours === 1) return "1h";
  return `${diffHours}h`;
};

export const generateQueryString = (params: { [key: string]: any }): string => {
  const queryParams = [];

  for (const key in params) {
    const value = params[key];
    if (value || value === 0 || value === false) {
      queryParams.push(`${key}=${value}`);
    }
  }

  if (queryParams.length > 0) {
    return `?${queryParams.join("&")}`;
  } else {
    return "";
  }
};

export function splitFullName(fullName: string) {
  const parts = fullName.trim().split(" ");
  const firstName = parts[0];
  const lastName = parts.length > 1 ? parts.slice(1).join(" ") : "";
  return { firstName, lastName };
}

export const formatDateToReadable = (dateString: string) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return "No date available";
  }

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const generateRandomText = (length: number = 12) => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};
/**
 * Creates a paste handler that sets a random string to a given form field
 * @param setForm - React state setter for form object
 * @param key - form key to update (e.g. "password" or "confirm_password")
 * @param length - length of the random string (default 12)
 */
export const createFormFieldPasteHandler = <T>(
  setState: React.Dispatch<React.SetStateAction<T>>,
  key?: keyof T,
  length = 12
) => {
  return (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const randomText = generateRandomText(length);

    setState((prev) => {
      if (typeof prev === "string") {
        return randomText as T;
      } else if (key) {
        return {
          ...prev,
          [key]: randomText,
        };
      }
      return prev;
    });
  };
};

export const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

export const convertArrayToObjects = (data: string[][]) => {
  const [headers, ...rows] = data;
  return rows.map((row) =>
    headers.reduce((obj, key, index) => {
      obj[key] = row[index];
      return obj;
    }, {} as Record<string, string>)
  );
};

export function formatDateTime(datetime: string): string {
  const date = new Date(datetime);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

export function cleanPermissionName(input: string): string {
  const words = input.trim().toLowerCase().split(" ");
  const lastTwoWords = words.slice(-2); // Get the last two words
  return lastTwoWords.join(" ");
}

export const validatePasswordCriteria = (password: string): boolean => {
  const checks = [
    (pw: string) => pw.length >= 8,
    (pw: string) => /[A-Z]/.test(pw),
    (pw: string) => /[a-z]/.test(pw),
    (pw: string) => /[0-9]/.test(pw),
    (pw: string) => /[!@#$%^&*]/.test(pw),
  ];
  return checks.every((check) => check(password));
};


export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export function getTimeAgo(timestamp: string): string {
  const now = new Date();
  const past = new Date(timestamp);
  const diffMs = now.getTime() - past.getTime();

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (years > 0) return `${years}y`;
  if (months > 0) return `${months}mo`;
  if (weeks > 0) return `${weeks}w`;
  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
}

export const getMessageTime = (timestamp: number) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};


// utils/downloadFile.ts
export const downloadFileViaProxy = async (fileUrl: string, filename: string) => {
  try {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1];

    const response = await fetch(fileUrl, {
      headers: {
        Authorization: `Bearer ${token ?? ""}`,
      },
    });

    if (!response.ok) {
      throw new Error("File download failed");
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Error downloading file:", err);
    alert("Could not download the file.");
  }
};

// utils/helper.ts
export const getUniqueValues = <T>(data: T[], key: keyof T): string[] => {
  return Array.from(new Set(data.map((item) => String(item[key])))).filter(Boolean);
};

